import {
  ComponentRendering,
  Field,
  Image,
  ImageField,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { icon_arrow_right } from '@gn-audio/icons';
import { ComponentProps, DynamicHeading, JabraIcon } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type SAPromoSpotStaticProps = {
  promoCards: SAPromoSpotCard[];
  headline: Field<string>;
  description: Field<string>;
  eyebrowText: Field<string>;
  isMainHeading: Field<boolean>;
  trackingSpotName?: Field<string>;
};

export type SAPromoSpotProps = {
  promoCards: SAPromoSpotCard[];
  headline: string;
  description: string;
  eyebrowText: Field<string>;
  isMainHeading: Field<boolean>;
  trackingSpotName?: Field<string>;
} & ComponentProps;

export type SAPromoSpotCard = {
  backgroundImage: ImageField;
  logo: ImageField;
  text: Field<string>;
  buttonLink: LinkField;
  buttonLinkAriaLabel?: Field<string>;
  buttonLinkTrackingName?: Field<string>;
  id: string;
};

type MappedSAPromoSpotCard = {
  backgroundImgSrc: string;
  backgroundImgDisplayName: string;
  logoSrc: string;
  logoDisplayName: string;
  text: string;
  buttonLink: string;
  buttonLinkTrackingName: string;
  buttonLinkAriaLabel?: string;
  id: string;
};

type SAPromoCardsProps = {
  items: MappedSAPromoSpotCard[];
};

const SAPromoCards = ({ items }: SAPromoCardsProps) => {
  return (
    <div className="mt-8 flex basis-full flex-col gap-4 md:flex-row">
      {items.map((item) => {
        const CardContent = () => (
          <article className="relative flex h-[320px] w-full flex-col items-center justify-center rounded-2xl bg-blue-900 md:h-[540px]">
            {item.backgroundImgSrc && (
              <img
                src={item.backgroundImgSrc}
                alt={item.backgroundImgDisplayName}
                className="absolute inset-0 z-0 size-full rounded-2xl object-cover object-center"
                loading="lazy"
              />
            )}

            {item.logoSrc && (
              <div className="absolute left-0 top-10 z-10 flex h-[70px] w-[178px] items-center justify-center rounded-r-full bg-white md:top-16">
                <Image
                  className="h-[70px] w-auto"
                  field={{
                    src: item.logoSrc,
                    alt: item.logoDisplayName,
                  }}
                  loading="lazy"
                />
              </div>
            )}
            <div
              className="relative z-10 text-2xl font-bold text-white"
              dangerouslySetInnerHTML={{ __html: item.text as string }}
            />

            {item.buttonLink && (
              <div className="absolute bottom-0 z-10 flex w-full justify-end pb-4 pr-4">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary">
                  <JabraIcon className="size-6 text-on-brand filter" Icon={icon_arrow_right} />
                </div>
              </div>
            )}
          </article>
        );

        return item.buttonLink ? (
          <a
            key={item.id}
            href={item.buttonLink}
            className="w-full rounded-2xl hover:no-underline md:pb-0 md:pt-0"
            {...(item.buttonLinkTrackingName && {
              'data-wt-name': item.buttonLinkTrackingName,
            })}
            {...(item.buttonLinkAriaLabel && {
              'aria-label': item.buttonLinkAriaLabel,
            })}
          >
            <CardContent />
          </a>
        ) : (
          <div key={item.id} className="w-full rounded-2xl md:pb-0 md:pt-0">
            <CardContent />
          </div>
        );
      })}
    </div>
  );
};

const SAPromoSpot: React.FC<SAPromoSpotProps> = (props) => {
  const { promoCards, headline, description, eyebrowText, isMainHeading, trackingSpotName } = props;
  const spotRef = useSpotTracking(trackingSpotName?.value);

  const refinedPromoCards = promoCards?.map((card) => ({
    backgroundImgSrc: card.backgroundImage?.value?.src || '',
    backgroundImgDisplayName: (card.backgroundImage?.value?.displayName as string) || '',
    logoSrc: card.logo?.value?.src || '',
    logoDisplayName: (card.logo?.value?.displayName as string) || '',
    text: sanitize((card.text?.value as string) || ''),
    buttonLink: card.buttonLink?.value?.href || '',
    buttonLinkTrackingName: card.buttonLinkTrackingName?.value || '',
    buttonLinkAriaLabel: card.buttonLinkAriaLabel?.value || '',
    id: card.id,
  }));

  return (
    <div ref={spotRef} data-wt-spot={trackingSpotName?.value} className="container-fluid">
      <div className="container mb-0 !py-0 text-center">
        {eyebrowText?.value && <Eyebrow className="mb-5" text={eyebrowText.value} align="center" />}

        {!!headline && (
          <DynamicHeading isMainHeading={isMainHeading?.value} className="heading-2">
            <span dangerouslySetInnerHTML={{ __html: headline }} />
          </DynamicHeading>
        )}

        {!!description && (
          <div className="paragraph-large" dangerouslySetInnerHTML={{ __html: description }} />
        )}
        <SAPromoCards items={refinedPromoCards} />
      </div>
    </div>
  );
};

export const getStaticProps = async (rendering: ComponentRendering<SAPromoSpotStaticProps>) => {
  const { promoCards, headline, description, eyebrowText, isMainHeading, trackingSpotName } =
    rendering?.fields || {};

  const sanitizedHeadline = sanitize((headline?.value as string) || '');
  const sanitizedDescription = sanitize((description?.value as string) || '');

  return {
    promoCards,
    headline: sanitizedHeadline,
    description: sanitizedDescription,
    eyebrowText,
    isMainHeading,
    trackingSpotName,
  };
};

export default withDatasourceCheck()(SAPromoSpot);
